import {classNames, layout, PushButton, theme, useBoxes, View} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useState} from "react";
import {stylesheet} from "typestyle";
import {BookingCodeDisplay} from "./BookingCodeDisplay";
import {DeliveryMethod, getMethodInfo} from "./delivery-method";
import {BookingCodeEmail} from "./BookingCodeEmail";
import {InfoBox} from "./InfoBox";
import {MainButton} from "./MainButton";
import {BookingCodeSms} from "./BookingCodeSms";
import {BookingCodeVerification} from "./BookingCodeVerification";
import {useLocalization} from "../../i18n";
import {BookingCodeManual} from "./BookingCodeManual";

interface BookingCodeStepProps {
    methods: DeliveryMethod[];
    boxNumber: string,
    code: string;
    onVerified: (contactData: ContactData, manualCode?: string) => any;
    onBack: () => any;
}

export interface ContactData {
    email?: string;
    phone?: string;
}

export function BookingCodeStep({methods, boxNumber, code, onVerified, onBack}: BookingCodeStepProps) {
    const {t} = useLocalization();
    const boxes = useBoxes();
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [method, setMethod] = useState(() => methods.length == 1 ? [...methods].pop() : undefined);
    const [verify, setVerify] = useState(false);

    const manualCodePicker = Boolean(boxes.find(b => b.number === boxNumber && b.charger));

    const handleVerified = useCallback((manualCode?: string) => {
        onVerified({
            email,
            phone,
        }, manualCode);
    }, [onVerified, email, phone]);

    const handleEmailSent = useCallback(({email}) => {
        setEmail(email);
        setVerify(true);
    }, []);

    const handleCodeDisplayed = useCallback(() => {
        setVerify(true);
    }, []);

    const handlePhoneSent = useCallback(({phone}) => {
        setPhone(phone);
        setVerify(true);
    }, []);

    const handleMethodCancel = useCallback(() => {
        if (methods.length > 1) {
            setMethod(undefined);
        }
        else {
            onBack();
        }
    }, [methods, onBack]);

    const handleVerifyCancel = useCallback(() => setVerify(false), []);

    if (manualCodePicker) {
        return (
            <BookingCodeManual
                boxNumber={boxNumber}
                onBack={onBack}
                onNext={handleVerified}
            />
        )
    }

    if (verify) {
        return (
            <BookingCodeVerification
                code={code}
                onBack={handleVerifyCancel}
                onVerified={handleVerified}
            />
        );
    }

    switch (method) {
        case DeliveryMethod.phone:
            return (
                <BookingCodeSms
                    onSent={handlePhoneSent}
                    onCancel={handleMethodCancel}
                    code={code}
                />
            );

        case DeliveryMethod.email:
            return (
                <BookingCodeEmail
                    onSent={handleEmailSent}
                    onBack={handleMethodCancel}
                    code={code}
                />
            );

        case DeliveryMethod.display:
            return (
                <BookingCodeDisplay
                    onNext={handleCodeDisplayed}
                    onBack={handleMethodCancel}
                    code={code}
                />
            );

        default:
            return (
                <View>
                    <h1 className={styles.receiveBookingCode}>{t("ReceiveBookingCode")}</h1>
                    <div className={classNames(layout.horizontal, layout.center, layout.fill)}>
                        {methods.map(method => (
                            <MethodButton key={method} method={method} onClick={setMethod}/>
                        ))}
                    </div>
                    <div className={classNames(layout.horizontal, layout.spaceBetween, layout.alignEnd, styles.bottomBar)}>
                        <div>
                            <PushButton onClick={onBack}>
                                {t("Back")}
                            </PushButton>
                        </div>
                        <InfoBox className={classNames(theme.moreContrast, styles.infoBox)}>
                            {t("BookingCodeInfo")}
                        </InfoBox>
                    </div>
                </View>
            )
    }

}



interface MethodButtonProps {
    method: DeliveryMethod;
    onClick: (method: DeliveryMethod) => any;
}

function MethodButton({method, onClick}: MethodButtonProps) {
    const {t} = useLocalization();
    const handleClick = useCallback(() => onClick(method), [onClick, method]);
    const {icon, name, desc} = getMethodInfo(method);
    return (
        <div className={styles.methodButton}>
            <MainButton onClick={handleClick} label={t(name)} materialIcon={icon} className={styles.methodButtonMain}/>
            <p className={styles.methodDesc}>
                {t(desc)}
            </p>
        </div>
    );
}

const styles = stylesheet({
    infoBox: {
        padding: "0.5em 3em 0.5em 1em",
        width: "35em",
    },
    bottomBar: {
        padding: "0 0 1.5rem 2.5rem"
    },
    receiveBookingCode: {
        fontSize: "2rem",
        textAlign: "center"
    },
    methodDesc: {
        fontSize: "0.875rem",
        textAlign: "center"
    },
    methodButton: {
        width: "14em",
        margin: "0 2em"
    },
    methodButtonMain: {
        fontSize: "1.75rem",
    }
})